/**
 * TODO: Refactor these calls, fix types, and add tests.
 */
import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import axios, { type AxiosError } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { AuthorizeInputType, ConsumerIdentity, MrnData, SearchResponse, SearchResult } from '../../types/api';
import { ClientConfig } from '../../types/serverConfig';
import getAccessToken from '../../utils/AccessToken';

export enum ApiStatus {
    Success,
    Error,
}

type ApiSuccess<ResponseDataType> = {
    status: ApiStatus.Success;
    data: ResponseDataType;
};

type ApiError = {
    status: ApiStatus.Error;
    error?: Error;
};

type ApiResult<ResponseDataType> = Promise<ApiSuccess<ResponseDataType> | ApiError>;

export type CreateSsoAccountResponse = {
    azureId: string;
    healthSystemId: string;
    identity: string;
};

type MigrateAccountResponse = {
    result: CreateSsoAccountResponse | string;
    newPassword: string;
};

function is2xxResponse(status: number): boolean {
    return status >= 200 && status < 300;
}

/**
 * This correlationId should be shared between all the API calls (made by axios). It is important to not make outbound calls
 * outside of this script file.
 *
 * How it works:
 * The correlationId is renewed on every `searchAccounts`. Other calls will use the same correlationId.
 * By doing so, the correlationId relates to one search term, which translates to one patient.
 * Therefore, the DD corrId will show the lifecycle of one patient roughly.
 */
let globalCorrelationId = uuidv4();

/**
 * See apiRouter.ts `search()`.
 */
export const searchAccounts = async (
    searchTerm: string,
    region: string,
    instance: IPublicClientApplication,
    accounts: AccountInfo[],
    clientConfiguration: ClientConfig,
    sessionId: string,
    refreshCorrelationId = true, // By default, searching should refresh the correlationId for a new search term
): Promise<SearchResponse> => {
    const jwt = await getAccessToken(instance, accounts, clientConfiguration.b2cTenantConfig.scopes);
    if (refreshCorrelationId) {
        globalCorrelationId = uuidv4(); // Start of a new patient context
    }
    console.log(`Correlation ID: ${globalCorrelationId}`);
    console.log(`Session ID: ${sessionId}`);
    const res = await axios.get<SearchResponse>('/api/search', {
        headers: {
            Authorization: 'Bearer ' + jwt,
            accountId: searchTerm,
            region: region,
            'correlation-id': globalCorrelationId,
            'session-id': sessionId,
        },
    });

    return res.data;
};

// Expose only the fixed ambiguity message; other service errors keep the existing generic message.
function getMrnMatchError(error: AxiosError<{ statusCode?: string }>): Error | undefined {
    if (error.response?.data?.statusCode === 'Error_MultiplePatientsFound') {
        return new Error('More than one patient record matches this MRN. Unable to link this record.');
    }
    return undefined;
}

/**
 * Look up patient details by MRN; PAS discovers the unique matching EHR.
 */
export const getMrnData = async (
    mrn: string,
    instance: IPublicClientApplication,
    accounts: AccountInfo[],
    clientConfiguration: ClientConfig,
    sessionId: string,
): ApiResult<MrnData> => {
    const jwt = await getAccessToken(instance, accounts, clientConfiguration.b2cTenantConfig.scopes);
    console.log(`Correlation ID: ${globalCorrelationId}`);
    console.log(`Session ID: ${sessionId}`);
    try {
        const res = await axios.get<MrnData>('/api/mrnData', {
            headers: {
                Authorization: 'Bearer ' + jwt,
                mrn: mrn,
                'correlation-id': globalCorrelationId,
                'session-id': sessionId,
            },
        });
        if (is2xxResponse(res.status)) {
            return { status: ApiStatus.Success, data: res.data };
        } else {
            console.log('could not find mrn. Status: ' + res.status + ', response: ' + res.statusText);
            return { status: ApiStatus.Error };
        }
    } catch (error: any) {
        console.log("network request error with 'mrnData'. " + error.message);
        return { status: ApiStatus.Error, error: getMrnMatchError(error) };
    }
};

/**
 * Sends a verification code to the recipient, given an email. Goes directly to PIMS for the results.
 */
export const sendVerificationCodeToEmail = async (
    newEmail: string,
    language = 'en',
    instance: IPublicClientApplication,
    accounts: AccountInfo[],
    clientConfiguration: ClientConfig,
    sessionId: string,
): Promise<boolean | string> => {
    try {
        const jwt = await getAccessToken(instance, accounts, clientConfiguration.b2cTenantConfig.scopes);
        await axios.post(
            `${clientConfiguration.pimsApiUrl}/v1/util/code`,
            {
                mode: 'email',
                value: newEmail,
                brand: clientConfiguration.theming.emailBrand,
                language,
            },
            {
                headers: {
                    Authorization: 'Bearer ' + jwt,
                    'correlation-id': globalCorrelationId,
                    'session-id': sessionId,
                },
            },
        );
        return true;
    } catch (err: any) {
        return err.response ? err.response.data.message : err;
    }
};

/**
 * Link an SSO account using the same MRN and EHR as the confirmed patient lookup.
 */
export const linkRecord = async (
    mrn: string,
    ehrSystem: string,
    mychartUsername: string,
    healthSystemId: string,
    ssoUserId: string,
    instance: IPublicClientApplication,
    accounts: AccountInfo[],
    clientConfiguration: ClientConfig,
    sessionId: string,
): ApiResult<void> => {
    const jwt = await getAccessToken(instance, accounts, clientConfiguration.b2cTenantConfig.scopes);
    console.log(`Correlation ID: ${globalCorrelationId}`);
    console.log(`Session ID: ${sessionId}`);
    try {
        const res = await axios.post(
            '/api/linkRecord',
            {
                mrn,
                ehrSystem,
                mychartUsername,
                healthSystemId,
                ssoUserId,
            },
            {
                headers: {
                    Authorization: 'Bearer ' + jwt,
                    'correlation-id': globalCorrelationId,
                    'session-id': sessionId,
                },
            },
        );

        if (is2xxResponse(res.status)) {
            return { status: ApiStatus.Success, data: undefined };
        } else {
            console.log('could not link record. Status: ' + res.status + ', response: ' + res.statusText);
            return { status: ApiStatus.Error };
        }
    } catch (error: any) {
        console.log("network request error with 'linkRecord'. " + error.message);
        return { status: ApiStatus.Error, error: getMrnMatchError(error) };
    }
};

/**
 * See apiRouter.ts `authorizeUser()`. This is the case when the account is a Dexcare EHR-linked account.
 * It will create the MyChart account for the EHR patient and authorization them.
 */
export const authorizeByPECUser = async (
    data: AuthorizeInputType,
    instance: IPublicClientApplication,
    accounts: AccountInfo[],
    clientConfiguration: ClientConfig,
    sessionId: string,
): Promise<boolean> => {
    try {
        const jwt = await getAccessToken(instance, accounts, clientConfiguration.b2cTenantConfig.scopes);
        console.log(`Correlation ID: ${globalCorrelationId}`);
        console.log(`Session ID: ${sessionId}`);
        await axios.post(`/api/authorizeUser`, data, {
            headers: {
                Authorization: 'Bearer ' + jwt,
                'correlation-id': globalCorrelationId,
                'session-id': sessionId,
            },
        });
        return true;
    } catch (error: any) {
        console.log('network request error with authorizeByPECUser ' + error.message);
        return false;
    }
};

export type DeleteLinkInput = {
    ehrSystem: string;
    mrn?: string; // optional to support NPP
    healthSystemId: string;
    instance: IPublicClientApplication;
    accounts: AccountInfo[];
    clientConfiguration: ClientConfig;
    sessionId: string;
};

/**
 * See apiRouter.ts `deleteRecord()`.
 */
export const deleteRecord = async ({ ehrSystem, mrn, healthSystemId, instance, accounts, clientConfiguration, sessionId }: DeleteLinkInput) => {
    const jwt = await getAccessToken(instance, accounts, clientConfiguration.b2cTenantConfig.scopes);
    console.log(`Correlation ID: ${globalCorrelationId}`);
    console.log(`Session ID: ${sessionId}`);
    try {
        const res = await axios.post(
            '/api/deleteRecord',
            {
                ehrSystem: ehrSystem, // Removes the record linked to the EHR System
                mrn: mrn,
                healthSystemId: healthSystemId,
            },
            {
                headers: {
                    Authorization: 'Bearer ' + jwt,
                    'correlation-id': globalCorrelationId,
                    'session-id': sessionId,
                },
            },
        );

        if (is2xxResponse(res.status)) {
            return { status: ApiStatus.Success };
        } else {
            console.log('could not delete record. Status: ' + res.status + ', response: ' + res.statusText);
            return { status: ApiStatus.Error };
        }
    } catch (error: any) {
        console.log("network request error with 'deleteRecord'. " + error.message);
        return { status: ApiStatus.Error };
    }
};

/**
 * See apiRouter.ts `createAccount()`.
 */
export const migrateAccount = async (
    searchResults: SearchResult[] | undefined,
    instance: IPublicClientApplication,
    accounts: AccountInfo[],
    clientConfiguration: ClientConfig,
    sessionId: string,
): Promise<MigrateAccountResponse> => {
    try {
        const jwt = await getAccessToken(instance, accounts, clientConfiguration.b2cTenantConfig.scopes);
        console.log(`Correlation ID: ${globalCorrelationId}`);
        console.log(`Session ID: ${sessionId}`);
        const response = await axios.post(
            '/api/account',
            {
                myChartUsername: searchResults ? searchResults[0]?.mychartUserName : '',
                signUpApplication: 'PEC Admin Portal',
                myChartSystem: searchResults ? searchResults[0]?.system : '',
            },
            {
                headers: {
                    Authorization: 'Bearer ' + jwt,
                    'correlation-id': globalCorrelationId,
                    'session-id': sessionId,
                },
            },
        );

        return { result: response.data?.result, newPassword: response.data?.newPassword };
    } catch (error: any) {
        let retValue = '';
        if (error.response?.data?.name === 'EmailNotFoundOnFile') {
            // Handle Email not available on patient file
            retValue = 'EmailNotFoundOnFile';
        } else if (error.response?.data?.status == 409) {
            //Handle duplicate account issue while migrating account
            retValue = 'conflict';
        }
        return { result: retValue, newPassword: '' };
    }
};

/**
 * See apiRouter.ts `changeEmail()`.
 */
export const changeEmail = async (
    newEmail: string,
    userId: string,
    healthSystemId: string,
    instance: IPublicClientApplication,
    accounts: AccountInfo[],
    clientConfiguration: ClientConfig,
    verificationCode: string,
    sessionId: string,
): ApiResult<void> => {
    try {
        const jwt = await getAccessToken(instance, accounts, clientConfiguration.b2cTenantConfig.scopes);
        await axios.post(
            '/api/changeEmail',
            {
                ssoUserId: userId,
                newEmail: newEmail,
                verificationCode,
                healthSystemId,
            },
            {
                headers: {
                    Authorization: 'Bearer ' + jwt,
                    'correlation-id': globalCorrelationId,
                    'session-id': sessionId,
                },
            },
        );
        return {
            status: ApiStatus.Success,
            data: undefined,
        };
    } catch (err: any) {
        return {
            status: ApiStatus.Error,
            error: err.response?.data,
        };
    }
};

type ResetPasswordResponse = {
    result: string;
    newPassword: string;
};

/**
 * See apiRouter.ts `resetPassword()`.
 */
export const changePassword = async (
    azureId: string,
    healthSystemId: string,
    instance: IPublicClientApplication,
    accounts: AccountInfo[],
    clientConfiguration: ClientConfig,
    sessionId: string,
): Promise<ResetPasswordResponse> => {
    try {
        const jwt = await getAccessToken(instance, accounts, clientConfiguration.b2cTenantConfig.scopes);
        const response = await axios.post(
            '/api/resetPassword',
            {
                ssoUserId: azureId,
                healthSystemId: healthSystemId,
            },
            {
                headers: {
                    Authorization: 'Bearer ' + jwt,
                    'correlation-id': globalCorrelationId,
                    'session-id': sessionId,
                },
            },
        );
        return { result: 'success', newPassword: response.data.newPassword };
    } catch (err: any) {
        console.error(err);
        return { result: err.response.data.message, newPassword: '' };
    }
};

/**
 * See apiRouter.ts `resetMFA()`.
 */
export const resetMFAInfo = async (
    useridvalue: string,
    instance: IPublicClientApplication,
    accounts: AccountInfo[],
    clientConfiguration: ClientConfig,
    sessionId: string,
): Promise<ApiStatus> => {
    try {
        const jwt = await getAccessToken(instance, accounts, clientConfiguration.b2cTenantConfig.scopes);
        const correlationId = uuidv4();
        console.log(`Correlation ID: ${correlationId}`);
        console.log(`Session ID: ${sessionId}`);
        const data = {
            userId: useridvalue,
        };
        await axios.post(`/api/reset`, data, {
            headers: {
                Authorization: 'Bearer ' + jwt,
                'correlation-id': correlationId,
                'session-id': sessionId,
            },
        });
        return ApiStatus.Success;
    } catch (error: any) {
        return ApiStatus.Error;
    }
};

/**
 * See auditRouter.ts `sendAudit()`.
 */
export const auditPageView = async (instance: any, accounts: any, clientConfiguration: any, sessionId: string) => {
    const jwt = await getAccessToken(instance, accounts, clientConfiguration.b2cTenantConfig.scopes);
    const correlationId = uuidv4();
    console.log(`Correlation ID: ${correlationId}`);
    console.log(`Session ID: ${sessionId}`);
    axios.post(
        '/api/audit',
        {},
        {
            headers: {
                Authorization: 'Bearer ' + jwt,
                'correlation-id': correlationId,
                'session-id': sessionId,
            },
        },
    );
};
